<template>
  <figure
    :class="['zg-teaser', {
      'zg-teaser--reverse-content': reverseContent
    }]"
  >
    <div
      v-if="image || illustration"
      class="image"
    >
      <img
        v-if="image"
        :alt="image.title"
        :src="image.src || image.src.default"
        loading="lazy"
      >

      <zg-illustration
        v-else-if="illustration"
        :illustration="illustration"
      />
    </div>

    <figcaption
      :class="[ 'text', {
        'text--no-image': !image && !illustration
      }]"
    >
      <h3 v-if="title">
        {{ title }}
      </h3>

      <p
        v-if="!$slots.text && text"
        v-html="text"
      />

      <slot name="text" />

      <ul v-if="list && !listIsOrdered">
        <li
          v-for="item in list"
          :key="item"
        >
          <zg-icon
            :icon="iconType"
            :size="iconSize"
          />
          <p v-html="item" />
        </li>
      </ul>

      <ol v-if="list && listIsOrdered">
        <li
          v-for="item in list"
          :key="item"
        >
          <p v-html="item" />
        </li>
      </ol>

      <div v-if="linkText && url">
        <a
          :href="url"
          @click="$emit('tracking', trackingObject || {})"
        >
          <zg-button
            :icons="{ right: 'arrow-forward' }"
            size="xlarge"
          >
            {{ linkText }}
          </zg-button>
        </a>
      </div>

      <p
        v-if="disclaimer"
        class="disclaimer"
        v-html="disclaimer"
      />
    </figcaption>
  </figure>
</template>

<script>
  import ZgButton from '../ZgButton/ZgButton.vue'
  import ZgIcon from '../ZgIcon/ZgIcon.vue'
  import ZgIllustration from '../ZgIllustration/ZgIllustration'

  export default {
    name: 'ZgTeaser',
    components: {
      ZgIllustration,
      ZgButton,
      ZgIcon
    },
    props: {
      image: {
        type: Object,
        required: false,
        default: null,
        validator: value => value.title && value.src
      },
      illustration: {
        type: String,
        required: false,
        default: null
      },
      title: {
        type: String,
        required: false,
        default: null
      },
      text: {
        type: String,
        required: false,
        default: null
      },
      list: {
        type: Array,
        required: false,
        default: null
      },
      listIsOrdered: {
        type: Boolean,
        required: false,
        default: false
      },
      linkText: {
        type: String,
        required: false,
        default: null
      },
      url: {
        type: String,
        required: false,
        default: null
      },
      disclaimer: {
        type: String,
        required: false,
        default: null
      },
      reverseContent: {
        type: Boolean,
        required: false,
        default: false
      },
      trackingObject: {
        type: Object,
        required: false,
        default: null,
        validator: value => value.event && value.category && value.action && value.label
      },
      iconType: {
        type: String,
        required: false,
        default: 'check'
      },
      iconSize: {
        type: String,
        required: false,
        default: 'small',
        validator: value => [
          'xsmall',
          'small',
          'medium',
          'large'
        ].includes(value)
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-teaser {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: rem(38px);
    text-align: left;

    @include mq(medium) {
      flex-direction: row;
    }

    .image {
      display: flex;
      align-items: flex-start;
      justify-content: center;
      flex-shrink: 0;
      width: rem(160px);
      height: rem(160px);
      text-align: left;

      @include mq(medium) {
        width: rem(320px);
        height: rem(320px);
      }

      img,
      ::v-deep svg {
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
      }
    }

    .text {
      flex-grow: 1;

      h3,
      p,
      ul {
        margin: 0 0 rhythm();
      }

      ul {
        list-style-type: none;
        padding: 0;

        li {
          display: flex;
          align-items: center;
          margin: 0 0 rhythm(small);

          &:last-child {
            margin: 0;
          }

          p {
            margin: 0 0 0 rhythm(xsmall);
          }

          svg {
            flex-shrink: 0;
          }
        }
      }

      ol {
        li {
          p {
            margin-left: rhythm(xsmall);
          }
        }
      }

      .disclaimer {
        @include type-body-xs;

        margin: rhythm() 0;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // modifiers
  // ? ----------------------------------------------------------------------

  .zg-teaser--reverse-content {
    @include mq(medium) {
      flex-direction: row-reverse;
    }
  }
</style>
